import React from "react";
import { Box, Card, CardContent, CircularProgress, Container, Grid, Link, Typography } from "@mui/material";
import Logo from "../Yavrio-CMYK.svg";
import { PAGE_TEXT, TERMS_PAGE_ADDRESS } from "./paymentPage.constants";
import { VIEW_KIND } from "./paymentPage.views";
import { runPaymentPageReload } from "./paymentPage.flow";
import PaymentPageStatus from "./PaymentPageStatus";
import PaymentPageOrderSummary from "./PaymentPageOrderSummary";

const GRID_SPACING = 4;
const LOGO_STYLE = Object.freeze({ height: 40 });
const PAGE_STYLE = Object.freeze({ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f5f5f5" });
const HEADER_STYLE = Object.freeze({ bgcolor: "#fff", borderBottom: "1px solid #e0e0e0", py: 2 });
const CONTENT_STYLE = Object.freeze({ mt: 4, pb: 8, flex: 1, display: "flex", flexDirection: "column" });
const GRID_STYLE = Object.freeze({ flex: 1 });
const PAYMENT_COLUMN_SIZE = Object.freeze({ xs: 12, md: 8 });
const PAYMENT_COLUMN_STYLE = Object.freeze({ display: "flex" });
const PAYMENT_CARD_STYLE = Object.freeze({ p: 2, flex: 1 });
const PAYMENT_CARD_CONTENT_STYLE = Object.freeze({ display: "flex", flexDirection: "column", height: "100%" });
const TITLE_STYLE = Object.freeze({ mb: 2, fontWeight: 600 });
const LOADING_PANEL_STYLE = Object.freeze({ display: "flex", justifyContent: "center", alignItems: "end", flex: 1 });
const LOADING_CONTENT_STYLE = Object.freeze({ textAlign: "center" });
const LOADING_TEXT_STYLE = Object.freeze({ mt: 2 });
const TERMS_LINK_STYLE = Object.freeze({ mt: 2, textAlign: "center" });

const DROPIN_CONTAINER_STYLE = Object.freeze({
    SHOWN: Object.freeze({ width: "100%", flex: 1, display: "block" }),
    HIDDEN: Object.freeze({ width: "100%", flex: 1, display: "none" }),
});

function resolveDropinContainerStyle(pageView) {
    if (pageView.kind === VIEW_KIND.PAYMENT_FORM) {
        return DROPIN_CONTAINER_STYLE.SHOWN;
    }
    return DROPIN_CONTAINER_STYLE.HIDDEN;
}

function PaymentDetailsTitle({ pageView }) {
    if (pageView.isTitleVisible === false) {
        return <></>;
    }
    return (
        <Typography variant="h6" sx={TITLE_STYLE}>
            {PAGE_TEXT.PAYMENT_DETAILS}
        </Typography>
    );
}

function LoadingPanel() {
    return (
        <Box sx={LOADING_PANEL_STYLE}>
            <Box sx={LOADING_CONTENT_STYLE}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={LOADING_TEXT_STYLE}>
                    {PAGE_TEXT.LOADING}
                </Typography>
            </Box>
        </Box>
    );
}

function PaymentFormPanel() {
    return <></>;
}

const PANEL_BY_VIEW_KIND = Object.freeze({
    [VIEW_KIND.LOADING]: LoadingPanel,
    [VIEW_KIND.PAYMENT_FORM]: PaymentFormPanel,
    [VIEW_KIND.STATUS]: PaymentPageStatus,
});

function TermsLink() {
    return (
        <Typography variant="caption" color="text.secondary" sx={TERMS_LINK_STYLE}>
            <Link href={TERMS_PAGE_ADDRESS} color="inherit" underline="hover">
                {PAGE_TEXT.TERMS_LINK}
            </Link>
        </Typography>
    );
}

function PaymentPageLayout({ pageView, orderSummary, dropinContainer, onTryAgain }) {
    const PagePanel = PANEL_BY_VIEW_KIND[pageView.kind];
    return (
        <Box sx={PAGE_STYLE}>
            <Box sx={HEADER_STYLE}>
                <Container maxWidth="lg">
                    <img src={Logo} alt={PAGE_TEXT.LOGO_ALTERNATIVE} style={LOGO_STYLE} />
                </Container>
            </Box>
            <Container maxWidth="lg" sx={CONTENT_STYLE}>
                <Grid container spacing={GRID_SPACING} sx={GRID_STYLE}>
                    <Grid size={PAYMENT_COLUMN_SIZE} sx={PAYMENT_COLUMN_STYLE}>
                        <Card variant="outlined" sx={PAYMENT_CARD_STYLE}>
                            <CardContent sx={PAYMENT_CARD_CONTENT_STYLE}>
                                <PaymentDetailsTitle pageView={pageView} />
                                <PagePanel
                                    pageView={pageView}
                                    onTryAgain={onTryAgain}
                                    onReload={runPaymentPageReload}
                                />
                                <Box ref={dropinContainer} sx={resolveDropinContainerStyle(pageView)} />
                                <TermsLink />
                            </CardContent>
                        </Card>
                    </Grid>
                    <PaymentPageOrderSummary pageView={pageView} orderSummary={orderSummary} />
                </Grid>
            </Container>
        </Box>
    );
}

export default PaymentPageLayout;
